<template>
  <main class="ai-store">
    <v-alert v-if="access.error || checkoutError" type="error" outlined role="alert">
      {{ checkoutError || access.error }}
      <v-btn text small :loading="confirming" @click="retryAccess">Revisar estado</v-btn>
    </v-alert>
    <v-alert v-if="checkoutNotice" type="info" outlined role="status">{{ checkoutNotice }}</v-alert>
    <header class="ai-heading">
      <div><h1>Fresher AI</h1><p>Cuatro agentes. Un solo espacio para hacer crecer tu operación.</p></div>
      <span class="ai-badge">Inteligencia para tu empresa</span>
    </header>
    <section class="ai-hero" aria-labelledby="ai-welcome">
      <div class="ai-hero-icon"><v-icon size="34" color="#1976d2">mdi-shield-check-outline</v-icon></div>
      <div class="ai-hero-copy">
        <span class="ai-eyebrow">CONOCE ANTES DE CONFIAR</span>
        <h2 id="ai-welcome">Tu próxima decisión, con más información</h2>
        <p>Conoce a tus clientes y proveedores con el Agente de Riesgo. Consulta personas y empresas, identifica señales relevantes y reúne sus resultados en un reporte.</p>
      </div>
      <div class="ai-actions">
        <v-btn rounded depressed color="primary" :disabled="!packs.length || purchaseDisabled" :loading="buying === 'pack:' + (packs[0] && packs[0].code)" @click="startCheckout('pack', packs[0])">Comprar créditos<v-icon right small>mdi-arrow-right</v-icon></v-btn>
      </div>
    </section>
    <section aria-labelledby="ai-agents-title">
      <div class="ai-section-heading"><h2 id="ai-agents-title">Un agente para cada operación clave</h2><span>Todos los agentes se habilitarán próximamente</span></div>
      <div class="ai-agents">
        <article v-for="agent in agents" :key="agent.id" class="ai-agent" :class="{ 'ai-agent-live': agent.available }">
          <div class="ai-agent-top">
            <span class="ai-agent-icon" :style="{ backgroundColor: agent.background }"><v-icon :color="agent.color" size="24">{{ agent.icon }}</v-icon></span>
            <span class="ai-status" :class="{ 'ai-status-live': agent.available }">{{ agent.available ? 'Disponible' : 'Próximamente' }}</span>
          </div>
          <h3>{{ agent.name }}</h3><p>{{ agent.description }}</p>
          <ul><li v-for="feature in agent.features" :key="feature"><v-icon size="15" :color="agent.available ? '#16805d' : '#8d9caf'">mdi-check</v-icon>{{ feature }}</li></ul>
          <span v-if="agent.available" class="ai-agent-link">Accede con un plan o créditos <v-icon size="16" color="#1976d2">mdi-shield-check-outline</v-icon></span>
          <span v-else class="ai-coming">En desarrollo <v-icon size="15" color="#8d9caf">mdi-clock-outline</v-icon></span>
        </article>
      </div>
    </section>
    <section aria-labelledby="ai-purchase-title">
      <div class="ai-section-heading"><div><h2 id="ai-purchase-title">Planes y créditos</h2><p>Las opciones de suscripción y créditos estarán disponibles próximamente.</p></div></div>
      <div class="ai-purchase-grid">
        <div class="ai-purchase-card">
          <div class="ai-purchase-title"><h3>Suscripción Fresher AI</h3><span class="ai-status ai-status-live" v-if="starterPlan">Disponible</span></div>
          <div v-if="starterPlan" class="ai-options">
            <div><strong>Plan mensual</strong><span>{{ planCredits }} créditos mensuales</span></div>
          </div>
          <p v-else class="ai-note">{{ loading ? 'Cargando plan…' : 'El plan mensual no está disponible por el momento.' }}</p>
          <v-btn small rounded depressed color="primary" :disabled="!starterPlan || purchaseDisabled" :loading="buying === 'plan:' + (starterPlan && starterPlan.code)" @click="startCheckout('plan', starterPlan)">Suscripción próximamente</v-btn>
          <p class="ai-note mt-3">Suscripción mensual recurrente. Más planes próximamente.</p>
        </div>
        <div class="ai-purchase-card">
          <div class="ai-purchase-title"><h3>Créditos a tu ritmo</h3><v-icon size="20" color="#1976d2">mdi-wallet-outline</v-icon></div>
          <div v-if="loading" class="ai-catalog-message" role="status"><v-progress-circular indeterminate size="18" width="2" color="primary" /> Cargando paquetes…</div>
          <div v-else-if="catalogError" class="ai-catalog-message" role="alert"><span>{{ catalogError }}</span><button type="button" @click="loadPacks">Reintentar</button></div>
          <div v-else-if="packs.length" class="ai-options ai-credit-options"><div v-for="pack in packs" :key="pack.id"><strong>{{ formatNumber(pack.credits) }} <small>créditos</small></strong><span>Precio próximamente</span><v-btn small rounded depressed color="primary" :disabled="purchaseDisabled" :loading="buying === 'pack:' + pack.code" @click="startCheckout('pack', pack)">Próximamente</v-btn></div></div>
          <p v-else class="ai-catalog-message">Los paquetes estarán disponibles próximamente.</p>
          <p class="ai-note">Pago único. No necesitas una suscripción para usar tus créditos.</p>
        </div>
      </div>
    </section>
    <p class="ai-footnote"><v-icon size="15" color="#718198">mdi-information-outline</v-icon>Los agentes de Fresher AI se habilitarán próximamente.</p>
  </main>
</template>

<script>
import { aiAccess, refreshAiAccess } from '@/services/aiAccess';

export default {
  name: 'SubscriptionsHome',
  data() {
    return {
      loading: true, catalogError: '', packs: [],
      starterPlan: null, buying: '', checkoutError: '', checkoutNotice: '', confirming: false,
      agents: [
        { id: 'risk', name: 'Agente de Riesgo', available: false, icon: 'mdi-shield-check-outline', color: '#1976d2', background: '#eaf4fe', description: 'Evalúa con quién haces negocios antes de dar el siguiente paso.', features: ['Validación de personas y empresas', 'Consultas en México y Estados Unidos', 'Señales de riesgo y reportes'] },
        { id: 'collections', name: 'Agente de Cobranza', available: false, icon: 'mdi-phone-outline', color: '#7453b9', background: '#f2edfb', description: 'Da seguimiento a tu cartera y mantén a la vista cada compromiso de pago.', features: ['Priorización de cuentas', 'Seguimiento de promesas de pago', 'Alertas y recordatorios'] },
        { id: 'sales', name: 'Agente de Ventas', available: false, icon: 'mdi-bullhorn-outline', color: '#bd7832', background: '#fff4e8', description: 'Organiza tus oportunidades y acompaña a tus prospectos durante el proceso comercial.', features: ['Organización de prospectos', 'Comunicación personalizada', 'Seguimiento de oportunidades'] },
        { id: 'pricing', name: 'Inteligencia de Precios', available: false, icon: 'mdi-chart-line', color: '#258779', background: '#eaf6f2', description: 'Encuentra contexto para ajustar tus precios y cuidar el margen de tu operación.', features: ['Análisis de precios', 'Señales y oportunidades de mercado', 'Apoyo para decisiones de margen'] },
      ],
    };
  },
  computed: {
    access() { return aiAccess; },
    purchaseDisabled() { return true; },
    planCredits() { return this.formatNumber(Object.values(this.starterPlan?.monthlyCreditsConfig || {}).reduce((sum, value) => sum + Number(value || 0), 0)); },
  },
  mounted() { this.loadPacks(); this.retryAccess(); },
  methods: {
    apiUrl(path) { return `${URI}/api/v1/${this.$store.state.language || 'en'}/billing${path}`; },
    authConfig() { return { headers: { Authorization: `Bearer ${this.$store.state.sToken}` } }; },
    async retryAccess() {
      if (this.confirming) return;
      this.confirming = true;
      this.checkoutError = '';
      try {
        if (this.$route.query.checkout === 'success') {
          this.checkoutNotice = 'Verificando tu pago. No es necesario volver a comprar.';
          if (!this.$route.query.session_id) throw new Error('No recibimos el identificador del pago. Contacta a soporte antes de volver a comprar.');
          await DB.post(this.apiUrl('/reconcile-checkout-success'), { sessionId: this.$route.query.session_id }, this.authConfig());
        }
        const allowed = await refreshAiAccess();
        if (allowed) {
          await this.$router.replace('/dashboardAI');
        } else if (this.$route.query.checkout === 'success') {
          this.checkoutError = 'Tu acceso todavía no está confirmado. Puedes revisar el estado nuevamente; no realices otro pago.';
        } else if (this.$route.query.checkout === 'cancel') {
          this.checkoutNotice = 'Saliste del proceso de pago. Puedes elegir un paquete cuando estés listo.';
        }
      } catch (error) {
        this.checkoutError = error.response?.data?.message || error.message || 'No pudimos confirmar tu pago. Revisa el estado antes de volver a comprar.';
      } finally { this.confirming = false; }
    },
    async startCheckout(kind, item) {
      if (this.purchaseDisabled || !item?.code) return;
      this.buying = `${kind}:${item.code}`;
      this.checkoutError = '';
      try {
        const response = await DB.post(this.apiUrl(kind === 'plan' ? '/subscription-checkout' : '/credit-pack-checkout'), {
          [kind === 'plan' ? 'planCode' : 'packCode']: item.code,
          frontendBaseUrl: window.location.origin,
        }, this.authConfig());
        const destination = new URL(response.data.checkoutUrl);
        if (destination.protocol !== 'https:' || destination.hostname !== 'checkout.stripe.com') throw new Error('No se recibió una dirección válida de Stripe.');
        window.location.assign(destination.href);
      } catch (error) {
        this.checkoutError = error.response?.data?.message || 'No pudimos abrir el pago en Stripe. Intenta nuevamente.';
        this.buying = '';
      }
    },
    formatNumber(value) { return Number(value).toLocaleString('es-MX'); },
    formatMoney(cents, currency) {
      return '$' + (Number(cents) / 100).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ' + String(currency || 'USD').toUpperCase();
    },
    async loadPacks() {
      this.loading = true;
      this.catalogError = '';
      this.packs = [];
      this.starterPlan = null;
      try {
        const [response, plansResponse] = await Promise.all([
          DB.get(this.apiUrl('/credit-packs'), this.authConfig()),
          DB.get(this.apiUrl('/plans'), this.authConfig()),
        ]);
        const matches = (plansResponse.data.plans || []).filter((plan) => Number(plan.amountCents) === 10000 && String(plan.currency).toLowerCase() === 'usd');
        this.starterPlan = matches.length === 1 ? matches[0] : null;
        if (!Array.isArray(response.data.creditPacks)) throw new Error('Invalid catalog');
        this.packs = response.data.creditPacks.map((pack) => ({
          id: pack.id, code: pack.code, amountCents: pack.amountCents, currency: pack.currency,
          credits: Object.values(pack.creditsConfig || {}).reduce((sum, value) => sum + Number(value || 0), 0),
        }));
      } catch (error) {
        this.catalogError = 'No pudimos cargar los paquetes.';
      } finally { this.loading = false; }
    },
  },
};
</script>

<style scoped>
.ai-store { padding: 28px 40px 22px; max-width: 1740px; margin: 0 auto; color: #172133; font-family: 'montserrat-regular', sans-serif; }
.ai-store p, .ai-store span, .ai-store li, .ai-store a, .ai-store button, .ai-store small { font-family: 'montserrat-regular', sans-serif; }
.ai-store ::v-deep .v-btn, .ai-store ::v-deep .v-btn__content { font-family: 'montserrat-semibold', sans-serif; }
.ai-store h1, .ai-store h2, .ai-store h3, .ai-store p { margin: 0; }
.ai-store h1 { font-family: 'montserrat-bold'; font-size: 27px; line-height: 1.3; }
.ai-store h2 { font-family: 'montserrat-bold'; font-size: 17px; line-height: 1.4; }
.ai-store h3 { font-family: 'montserrat-semibold'; font-size: 15px; line-height: 1.5; }
.ai-store a { text-decoration: none; color: #1976d2; }
.ai-store a:focus-visible, .ai-store button:focus-visible { outline: 2px solid #1976d2; outline-offset: 4px; }
.ai-heading { display: flex; align-items: center; justify-content: space-between; gap: 20px; margin-bottom: 24px; }
.ai-heading p { color: #718198; font-size: 12px; margin-top: 7px; }
.ai-badge { background: #e8f3fe; color: #266194; border-radius: 24px; padding: 7px 12px; font-size: 11px; white-space: nowrap; }
.ai-hero { display: flex; align-items: center; gap: 24px; padding: 28px; background: white; border: 1px solid #e2e7ed; border-radius: 14px; }
.ai-hero-icon { display: grid; place-items: center; flex: 0 0 66px; height: 66px; border-radius: 22px; background: #eaf4fe; }
.ai-hero-copy { flex: 1; min-width: 0; }
.ai-eyebrow { font-size: 10px; letter-spacing: 1px; font-family: 'montserrat-bold'; color: #1976d2; }
.ai-hero h2 { font-size: 24px; margin-top: 6px; }
.ai-hero p { max-width: 780px; margin-top: 8px; color: #718198; font-size: 12px; line-height: 1.8; }
.ai-actions { display: flex; align-items: center; flex-direction: column; gap: 12px; }
.ai-actions a:not(.v-btn) { font-size: 11px; }
.ai-store .v-btn { text-transform: none; letter-spacing: .2px; font-size: 12px; }
.ai-section-heading { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin: 22px 0 13px; }
.ai-section-heading > span, .ai-section-heading p, .ai-section-heading > a { color: #718198; font-size: 11px; }
.ai-section-heading p { margin-top: 4px; }
.ai-section-heading > a { color: #1976d2; }
.ai-agents { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 18px; }
.ai-agent { display: flex; flex-direction: column; min-width: 0; padding: 20px; border: 1px solid #e2e7ed; border-radius: 12px; background: white; }
.ai-agent-live { border-color: #b4d7f5; box-shadow: 0 3px 12px #1976d209; }
.ai-agent-top { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 14px; }
.ai-agent-icon { display: grid; place-items: center; width: 42px; height: 42px; border-radius: 13px; }
.ai-status { font-size: 10px; background: #f0f3f7; color: #718198; border-radius: 20px; padding: 4px 9px; white-space: nowrap; }
.ai-status-live { background: #e8f6ef; color: #16724f; }
.ai-agent > p { font-size: 12px; line-height: 1.7; color: #718198; margin-top: 7px; }
.ai-agent ul { display: grid; gap: 8px; margin: 15px 0 18px; padding: 0; list-style: none; }
.ai-agent li { display: flex; align-items: flex-start; gap: 7px; font-size: 11px; line-height: 1.5; color: #526176; }
.ai-agent li .v-icon { flex-shrink: 0; margin-top: 1px; }
.ai-agent-link, .ai-coming { display: flex; justify-content: space-between; align-items: center; margin-top: auto; padding-top: 12px; border-top: 1px solid #edf0f5; font-size: 11px; }
.ai-coming { color: #8d9caf; }
.ai-purchase-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
.ai-purchase-card { padding: 18px 22px; border: 1px solid #e2e7ed; border-radius: 12px; background: white; min-width: 0; }
.ai-purchase-title { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.ai-options { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; margin: 16px 0; }
.ai-options > div { display: flex; flex-direction: column; gap: 5px; min-width: 0; }
.ai-options strong { font-family: 'montserrat-semibold', sans-serif; font-size: 12px; font-weight: 600; }
.ai-options span, .ai-credit-footer span { font-size: 11px; color: #718198; }
.ai-credit-options strong { font-size: 17px; font-family: 'montserrat-semibold'; }
.ai-credit-options small { font-family: inherit; font-size: 10px; font-weight: 400; }
.ai-note { font-size: 10px; color: #718198; line-height: 1.6; }
.ai-credit-footer { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.ai-catalog-message { display: flex; align-items: center; gap: 10px; margin: 18px 0 !important; font-size: 12px; color: #718198; }
.ai-catalog-message button { color: #1976d2; text-decoration: underline; }
.ai-footnote { display: flex; align-items: flex-start; justify-content: center; gap: 7px; color: #718198; font-size: 10px; line-height: 1.6; margin-top: 16px !important; }
@media (max-width: 1200px) {
  .ai-store { padding: 24px; }
  .ai-agents { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .ai-hero h2 { font-size: 21px; }
  .ai-purchase-grid { grid-template-columns: 1fr; }
}
@media (max-width: 650px) {
  .ai-store { padding: 20px 16px; }
  .ai-heading, .ai-section-heading, .ai-hero { align-items: flex-start; flex-direction: column; }
  .ai-heading { gap: 12px; }
  .ai-hero { padding: 22px; gap: 15px; }
  .ai-hero-icon { flex-basis: auto; width: 56px; height: 56px; }
  .ai-actions { align-items: flex-start; }
  .ai-agents, .ai-options { grid-template-columns: 1fr; }
  .ai-credit-footer { align-items: flex-start; flex-direction: column; }
}
</style>
